import React, { useState } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { getAmenityLabel, amenityIcons } from '../amenities';
import ClickableReviewsSummary, { type PublicReviewItem } from './ClickableReviewsSummary';
import MediaDisplay from './MediaDisplay';
import VideoPlayer from './VideoPlayer';
import type { Review } from '../models/CreateReviewRequest';
import type { SpaceSummary } from '../models/Space';
import { isVideoUrl } from '../utils/mediaUtils';
import {
  organizationContactHref,
  organizationContactIcon,
  organizationContactOpensInSameTab,
  visibleOrganizationContacts,
} from '../utils/organizationContact';
import { isSpace24hOpen } from '../utils/spaceUtils';

export interface PublicSpaceProfileHeaderProps {
  space: SpaceSummary;
  reviewStats?: { averageRating: number; reviewCount: number } | null;
  spaceReviews?: Review[] | PublicReviewItem[];
  spaceReviewsLoading?: boolean;
  /** When false, hide the contacts column (useful for multi-space org pages). Default true. */
  showContacts?: boolean;
  /** Heading level for the space name. Default h1. */
  titleComponent?: 'h1' | 'h2';
  ratingName?: string;
  /** When set, the card navigates here on click (e.g. `/spaces/:id` on org pages). */
  href?: string;
}

const PublicSpaceProfileHeader: React.FC<PublicSpaceProfileHeaderProps> = ({
  space,
  reviewStats = null,
  spaceReviews = [],
  spaceReviewsLoading = false,
  showContacts = true,
  titleComponent = 'h1',
  ratingName = 'school-space-rating',
  href,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  const spaceImages = space.imageUrls ?? [];
  const fullAddress = [space.addressLine1, space.addressLine2, space.city, space.zipCode]
    .filter(Boolean)
    .join(', ');
  const displayAddress = space.formattedAddress || fullAddress;
  const mapsUrl =
    space.googleMapsUrl?.trim() ||
    (displayAddress
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(displayAddress)}`
      : null);
  const visibleContacts = showContacts ? visibleOrganizationContacts(space.contacts) : [];
  const hasOrganizationContact = visibleContacts.length > 0;
  const isClickable = Boolean(href?.trim());

  const openSpace = () => {
    if (href?.trim()) navigate(href.trim());
  };

  const openGallery = (index: number) => setSelectedImageIndex(index);
  const closeGallery = () => setSelectedImageIndex(null);
  const showNextImage = () => {
    if (selectedImageIndex == null || spaceImages.length === 0) return;
    setSelectedImageIndex((selectedImageIndex + 1) % spaceImages.length);
  };
  const showPreviousImage = () => {
    if (selectedImageIndex == null || spaceImages.length === 0) return;
    setSelectedImageIndex((selectedImageIndex - 1 + spaceImages.length) % spaceImages.length);
  };

  return (
    <>
      <Paper
        elevation={2}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onClick={isClickable ? openSpace : undefined}
        onKeyDown={
          isClickable
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openSpace();
                }
              }
            : undefined
        }
        sx={{
          p: { xs: 1.5, md: 2 },
          borderRadius: 2,
          border: '1px solid rgba(255, 255, 255, 0.06)',
          ...(isClickable
            ? {
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: (theme) => theme.shadows[3],
                },
              }
            : {}),
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={0}
          alignItems="stretch"
          sx={{ width: '100%' }}
        >
          <Stack spacing={1.5} flex={hasOrganizationContact ? 5 : 1} minWidth={0}>
            <Grid container spacing={{ xs: 1.5, sm: 2 }} alignItems="flex-start">
              <Grid item xs={12} sm="auto">
                <Box
                  sx={{
                    width: { xs: '100%', sm: 160 },
                    height: { xs: 160, sm: 140 },
                    bgcolor: 'grey.900',
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    flexShrink: 0,
                  }}
                >
                  {spaceImages[0] ? (
                    <MediaDisplay
                      src={spaceImages[0]}
                      alt={space.name}
                      onClick={() => openGallery(0)}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        cursor: 'pointer',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                      }}
                    >
                      <MusicNoteIcon sx={{ fontSize: 48, opacity: 0.4 }} />
                    </Box>
                  )}
                </Box>
              </Grid>

              <Grid item xs>
                <Stack spacing={0.75}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography
                      variant="h4"
                      component={titleComponent}
                      sx={{ fontWeight: 800, fontSize: { xs: '1.25rem', sm: '1.6rem' }, lineHeight: 1.2 }}
                    >
                      {space.name}
                    </Typography>
                    {isSpace24hOpen(space.weeklySchedule) ? (
                      <Chip
                        icon={<MusicNoteIcon sx={{ fontSize: '0.75rem !important' }} />}
                        label={t('space.open24_7', '24/7 Open')}
                        size="small"
                        color="success"
                        sx={{ fontWeight: 600 }}
                      />
                    ) : null}
                  </Stack>

                  {displayAddress ? (
                    mapsUrl ? (
                      <Box
                        component="a"
                        href={mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(event) => event.stopPropagation()}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'flex-start',
                          gap: 0.75,
                          maxWidth: '100%',
                          color: 'text.secondary',
                          textDecoration: 'none',
                          cursor: 'pointer',
                          '&:hover': {
                            color: 'primary.main',
                          },
                        }}
                      >
                        <LocationOnIcon sx={{ fontSize: 18, mt: 0.2, flexShrink: 0 }} />
                        <Typography variant="body2" component="span" sx={{ color: 'inherit' }}>
                          {displayAddress}
                        </Typography>
                      </Box>
                    ) : (
                      <Stack direction="row" spacing={0.75} alignItems="flex-start">
                        <LocationOnIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.2 }} />
                        <Typography variant="body2" color="text.secondary">
                          {displayAddress}
                        </Typography>
                      </Stack>
                    )
                  ) : null}

                  <Box onClick={(event) => event.stopPropagation()}>
                    <ClickableReviewsSummary
                      averageRating={reviewStats?.averageRating ?? 0}
                      reviewCount={reviewStats?.reviewCount ?? 0}
                      reviews={spaceReviews as PublicReviewItem[]}
                      loading={spaceReviewsLoading}
                      ratingName={ratingName}
                      stopPropagation
                    />
                  </Box>

                  {space.description?.trim() ? (
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ whiteSpace: 'pre-line', lineHeight: 1.55 }}
                    >
                      {space.description.trim()}
                    </Typography>
                  ) : null}
                </Stack>
              </Grid>
            </Grid>

            {spaceImages.length > 1 ? (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {spaceImages.slice(1, 7).map((url, index) => {
                  const galleryIndex = index + 1;
                  return (
                    <Box
                      key={`${url}-${galleryIndex}`}
                      sx={{
                        width: { xs: 72, sm: 88 },
                        height: { xs: 72, sm: 88 },
                        borderRadius: 1.5,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'divider',
                        cursor: 'pointer',
                        flexShrink: 0,
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        openGallery(galleryIndex);
                      }}
                    >
                      <MediaDisplay
                        src={url}
                        alt={`${space.name} ${galleryIndex + 1}`}
                        showPlayButton={false}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </Box>
                  );
                })}
                {spaceImages.length > 7 ? (
                  <Box
                    onClick={(event) => {
                      event.stopPropagation();
                      openGallery(7);
                    }}
                    sx={{
                      width: { xs: 72, sm: 88 },
                      height: { xs: 72, sm: 88 },
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      bgcolor: 'action.hover',
                    }}
                  >
                    <Typography variant="body2" fontWeight={700}>
                      +{spaceImages.length - 7}
                    </Typography>
                  </Box>
                ) : null}
              </Stack>
            ) : null}

            {space.amenities?.length ? (
              <Box>
                <Typography variant="overline" color="text.secondary">
                  {t('space.amenities', 'Amenities')}
                </Typography>
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                  {space.amenities.map((amenity) => {
                    const key = amenity.amenityType;
                    const label = getAmenityLabel(key) || amenity.details;
                    const icon = amenityIcons[key] || <AddIcon fontSize="small" />;
                    return (
                      <Chip
                        key={`${key}-${amenity.details}`}
                        size="small"
                        icon={icon}
                        label={label}
                        sx={{
                          bgcolor: 'grey.800',
                          color: 'common.white',
                          '& .MuiChip-icon': { color: 'grey.300' },
                        }}
                      />
                    );
                  })}
                </Stack>
              </Box>
            ) : null}
          </Stack>

          {hasOrganizationContact ? (
            <>
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  display: { xs: 'none', md: 'block' },
                  ml: 3,
                  mr: 2,
                  borderColor: 'divider',
                  opacity: 0.35,
                }}
              />
              <Divider
                sx={{
                  display: { xs: 'block', md: 'none' },
                  my: 1.5,
                  borderColor: 'divider',
                  opacity: 0.35,
                }}
              />
              <Box sx={{ flex: 2, minWidth: { md: 148 } }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 0.75, lineHeight: 1.15 }}
                >
                  {t('space.contact', 'Contact')}
                </Typography>
                <Stack spacing={1}>
                  {visibleContacts.map((contact, index) => {
                    const ContactIcon = organizationContactIcon(contact);
                    const href = organizationContactHref(contact);
                    const opensInSameTab = organizationContactOpensInSameTab(contact);
                    return (
                      <Box
                        key={`contact-${contact.type}-${index}`}
                        component="a"
                        href={href}
                        target={opensInSameTab ? '_self' : '_blank'}
                        rel={opensInSameTab ? undefined : 'noopener noreferrer'}
                        onClick={(event) => event.stopPropagation()}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.25,
                          p: 1.25,
                          borderRadius: 1.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          textDecoration: 'none',
                          color: 'inherit',
                          transition: 'border-color 0.2s ease, background-color 0.2s ease',
                          '&:hover': {
                            bgcolor: 'action.hover',
                            borderColor: 'primary.main',
                          },
                        }}
                      >
                        <ContactIcon sx={{ color: 'primary.main', fontSize: 20, flexShrink: 0 }} />
                        <Box minWidth={0}>
                          <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                            {contact.value}
                          </Typography>
                          {contact.comment?.trim() ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ wordBreak: 'break-word' }}
                            >
                              {contact.comment.trim()}
                            </Typography>
                          ) : null}
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            </>
          ) : null}
        </Stack>
      </Paper>

      <Dialog open={selectedImageIndex !== null} onClose={closeGallery} maxWidth="lg" fullWidth>
        <DialogContent sx={{ p: 0, bgcolor: 'black', position: 'relative' }}>
          {selectedImageIndex !== null && spaceImages[selectedImageIndex] ? (
            <>
              {isVideoUrl(spaceImages[selectedImageIndex]) ? (
                <VideoPlayer
                  src={spaceImages[selectedImageIndex]}
                  alt={space.name}
                  sx={{ width: '100%', height: 'auto', maxHeight: '90vh' }}
                />
              ) : (
                <MediaDisplay
                  src={spaceImages[selectedImageIndex]}
                  alt={space.name}
                  showPlayButton={false}
                  sx={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '90vh',
                    objectFit: 'contain',
                  }}
                />
              )}
              {spaceImages.length > 1 ? (
                <>
                  <IconButton
                    onClick={showPreviousImage}
                    aria-label={t('common.previous', 'Previous')}
                    sx={{
                      position: 'absolute',
                      left: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(255, 255, 255, 0.8)',
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' },
                    }}
                  >
                    <KeyboardArrowLeft />
                  </IconButton>
                  <IconButton
                    onClick={showNextImage}
                    aria-label={t('common.next', 'Next')}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(255, 255, 255, 0.8)',
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' },
                    }}
                  >
                    <KeyboardArrowRight />
                  </IconButton>
                </>
              ) : null}
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PublicSpaceProfileHeader;
